import { createFileRoute } from "@tanstack/react-router";
import {
  IndustryCityMarketingPage,
  buildIndustryCityMarketingHead,
  type IndustryCityMarketingConfig,
} from "@/components/IndustryCityMarketingPage";
import { CASTLE_ROCK_PROFILE } from "@/lib/cityProfiles/castle-rock";

const CONFIG: IndustryCityMarketingConfig = {
  slug: "hvac-marketing-castle-rock",
  industry: "HVAC",
  industryLower: "hvac",
  ...CASTLE_ROCK_PROFILE,

  metaTitle: "HVAC Marketing in Castle Rock | SEO, Maps, Ads & Web",
  metaDescription:
    "Owner-led HVAC marketing for Castle Rock — SEO, Google Maps, web design, and ads tuned for Founders Village, The Meadows, and Castle Pines. Flat $1,650/mo, no contracts.",

  h1: {
    lead: "HVAC marketing in",
    gold: "Castle Rock",
    trail: "— booked from The Meadows to Crystal Valley",
  },
  eyebrow: "Castle Rock HVAC Marketing",
  heroDescription:
    "We help Castle Rock HVAC companies win the Map Pack, capture emergency-call SEO across 80104/80108/80109, and stay booked through the brutal swing seasons between Denver and the Springs.",

  intro:
    "Castle Rock is a top-five fastest-growing city in Colorado — new builds in The Meadows and Crystal Valley are filling up faster than HVAC supply can keep up, and homeowners in Founders Village and Castle Pines are replacing 20-year-old systems with heat pumps and high-efficiency furnaces. The HVAC companies winning here aren't the cheapest; they're the ones a homeowner in The Pinery actually finds first when their AC quits on a 96° July afternoon. That's the job: own the Map Pack across all three Castle Rock ZIPs, rank for 'AC repair Castle Rock' and 'furnace replacement near me,' and load a mobile site fast enough on Founders Parkway to actually get the call.",

  painPoints: [
    "Getting buried in the Map Pack by Denver-metro HVAC chains that don't even truck down to Castle Rock — but outrank you because their GBP is louder.",
    "Slow swing seasons (April–May, September–October) where calls dry up between the AC rush and the furnace rush.",
    "Losing high-ticket heat-pump and full-system replacement leads in The Meadows and Castle Pines to whichever company shows up first in a Google search on a phone.",
  ],

  services: {
    seo: "Rank for 'AC repair Castle Rock,' 'furnace replacement 80108,' and every neighborhood + service combo across Founders Village, The Meadows, Castle Pines, and Crystal Valley.",
    googleMaps: "Top-3 Map Pack for Castle Rock HVAC — proper categories, service-area radius tuned for 80104/80108/80109, geo-tagged photos, weekly GBP posts, and a real review-generation cadence.",
    webDesign: "Hand-coded HVAC sites that load sub-2s on a phone parked on Wolfensberger Rd and convert emergency callers with sticky tap-to-call and clean service-area copy.",
    ppc: "Profitable Google Ads tuned to Castle Rock seasons — emergency-repair bidding ramps in July and December, ROI-tracked per ZIP and per neighborhood.",
    geoAi: "Get cited when Castle Rock homeowners ask ChatGPT, Gemini, or Perplexity 'best HVAC company in Castle Rock' or 'who installs heat pumps near Founders Village.'",
    socialMedia: "Local trust-building content — install photos, before/after duct work, neighborhood-tagged maintenance reminders — that converts repeat repairs into annual contracts.",
  },

  trustPoints: [
    "30 minutes south of Castle Rock — we work your service area, we don't farm it out to a coastal call center.",
    "300+ Colorado clients served since 2006, including HVAC, plumbing, and electrical trades across the Front Range.",
    "Flat $1,650/month marketing, websites from $1,850 — published publicly, no discovery-call price reveal.",
    "Talk to Chris directly. No account managers, no sales reps, no offshore handoffs.",
  ],

  testimonial: {
    quote:
      "We were getting beat by Denver companies that don't even service Castle Rock. Within four months they had us top-3 in the Map Pack and our phone hasn't slowed down since. Straight shooters — exactly what I needed.",
    role: "HVAC company owner",
    location: "Castle Rock",
  },

  faqs: [
    {
      q: "How do I get more HVAC leads in Castle Rock?",
      a: "Local SEO + a tuned Google Business Profile is the highest-ROI path — show up in the Map Pack for 'AC repair Castle Rock' and 'furnace repair near me' across 80104, 80108, and 80109, backed by a website that converts mobile callers in under 3 seconds. Layer Google Ads on during swing seasons (April–May, September–October) to keep techs booked when calls naturally slow.",
    },
    {
      q: "How much does HVAC marketing cost in Castle Rock?",
      a: "Our Growth Marketing package — SEO, Google Maps, GEO AI, and social — is $1,650/month flat. A new HVAC website starts at $1,850 one-time, owned outright. In-house financing is available starting at 4 monthly payments. Prices are published publicly because Castle Rock owners deserve a straight number, not a discovery-call dance.",
    },
    {
      q: "How long until I rank in the Castle Rock Map Pack?",
      a: "Most Castle Rock HVAC clients see meaningful Map Pack movement in 60–120 days. Citywide top-3 across Founders Village, The Meadows, and Castle Pines usually takes 4–6 months of consistent GBP work, citation cleanup, and reviews. It compounds — month 6 is dramatically stronger than month 2.",
    },
    {
      q: "Do you cover the unincorporated areas like Sedalia and Larkspur?",
      a: "Yes. We build service-area copy and Map Pack signals for every Castle Rock corridor and the unincorporated areas your trucks reach — Sedalia, Larkspur, Franktown, Castle Pines Village. If your techs roll into a ZIP, we make sure that ZIP can find you.",
    },
    {
      q: "Should I run Google Ads or focus on SEO first?",
      a: "Need calls this week? Start Google Ads — we can have a tuned Castle Rock HVAC campaign live in 5 business days. Want a lead engine that doesn't shut off when you pause spend? Start SEO + Google Maps. Most successful Castle Rock HVAC clients run both: ads for immediate flow, SEO for the durable foundation.",
    },
    {
      q: "Are you actually local to Castle Rock?",
      a: "We're headquartered 30 minutes south in Colorado Springs (3210 E Woodmen Rd) and work Castle Rock weekly. Verified Google Business Profile, 4.9★ average, Colorado-owned since 2006. Stop by, meet Chris, or have him drive up — either works.",
    },
    {
      q: "Do you lock me into a contract?",
      a: "No. Every Colorado Web Impressions engagement is month-to-month. If we're not earning our keep, you cancel — no early-termination fees, no clawbacks. We earn the renewal every month.",
    },
  ],

  related: [
    {
      to: "/hvac-marketing-colorado",
      label: "HVAC marketing across Colorado",
      description: "Statewide HVAC marketing hub — every service we offer for Colorado HVAC companies.",
    },
    {
      to: "/castle-rock",
      label: "Castle Rock digital marketing hub",
      description: "Every service we offer for Castle Rock businesses in one place.",
    },
    {
      to: "/castle-rock-seo",
      label: "Castle Rock SEO",
      description: "Deep dive on local SEO for Castle Rock service businesses.",
    },
    {
      to: "/hvac-marketing-colorado-springs",
      label: "HVAC marketing in Colorado Springs",
      description: "Our flagship HVAC marketing page for El Paso County.",
    },
    {
      to: "/pricing",
      label: "Flat, public pricing",
      description: "$1,650/mo marketing, websites from $1,850 — plus in-house financing for new sites.",
    },
    {
      to: "/meet-chris",
      label: "Meet Chris",
      description: "The owner you'll actually talk to — not a salesperson or account manager.",
    },
  ],

  showPricing: true,
};

export const Route = createFileRoute("/hvac-marketing-castle-rock")({
  head: () => buildIndustryCityMarketingHead(CONFIG),
  component: () => <IndustryCityMarketingPage config={CONFIG} />,
});
